/**
 * Money is an integer number of shekels held as a string. Not a number, and
 * never a decimal: every amount a tutor types is whole, every total is a sum
 * of whole amounts, and receipts are official documents that must not carry
 * float error.
 */

export function toAmount(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  return String(Math.round(amount));
}

export function amountValue(amount: string | null | undefined): number {
  const value = Number(amount ?? 0);
  return Number.isFinite(value) ? Math.round(value) : 0;
}

export function sumAmounts(amounts: readonly string[]): string {
  return String(amounts.reduce((total, amount) => total + amountValue(amount), 0));
}

/**
 * The plain digits and the currency sign, grouped. Rendering is a separate
 * step, because a number beside Hebrew needs bidi isolation to survive; see
 * BidiUtil and the MoneyText component.
 */
export function formatAmount(amount: string | number | null | undefined, currency = '₪'): string {
  const value = amountValue(typeof amount === 'number' ? String(amount) : amount);
  return `${value.toLocaleString('en-US')} ${currency}`;
}

/**
 * VAT does not divide into whole shekels, so the one place decimals are real
 * is the VAT split on a receipt. It arrives from the server already frozen in
 * agorot, and is rendered from the integer rather than from a float, so
 * 18010 agorot is always 180.10 and never 180.09.
 */
export function formatAgorot(agorot: number | null | undefined, currency = '₪'): string {
  const value = Math.round(Number(agorot ?? 0));
  const sign = value < 0 ? '-' : '';
  const absolute = Math.abs(value);
  const shekels = Math.floor(absolute / 100).toLocaleString('en-US');
  return `${sign}${shekels}.${String(absolute % 100).padStart(2, '0')} ${currency}`;
}
